import type { RoleReference } from '@supply-mover-2-0/core';
import type {
	DocumentCopyEmptyRoleModel,
	DocumentCopyListModel,
} from '@supply-mover-2-0/shared';
import { CopyMode } from '@supply-mover-2-0/shared';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { ButtonGroup } from './ButtonGroup';
import { RecipientBucketEditor } from './RecipientBucketEditor';

/** The lines of an email a customer-document list can go on, most visible first. */
export const DOCUMENT_COPY_LINE_OPTIONS: ReadonlyArray<{
	value: CopyMode;
	label: string;
}> = [
	{ value: CopyMode.TO, label: 'To' },
	{ value: CopyMode.CC, label: 'CC' },
	{ value: CopyMode.BCC, label: 'BCC' },
];

/** What each line means to the customer receiving the email. */
const LINE_HINTS: Record<CopyMode, string> = {
	[CopyMode.TO]:
		'Addressed alongside the customer. The customer sees these addresses.',
	[CopyMode.CC]: 'Copied openly. The customer sees these addresses.',
	[CopyMode.BCC]: 'Copied blind. The customer does not see these addresses.',
};

export interface DocumentCopyListEditorProps {
	/** Unique per page: names the line picker's radio group. */
	name: string;
	list: DocumentCopyListModel;
	onChange: (list: DocumentCopyListModel) => void;
	availableRoles: Array<RoleReference>;
	/** Roles that currently contain nobody; only the ones this list selects show. */
	emptyRoles?: Array<DocumentCopyEmptyRoleModel>;
	/** Completes "No one is in this role, so …", e.g. "nobody will receive it". */
	emptyRoleConsequence: string;
	/** Anything an admin needs to know before choosing recipients. Shown first. */
	detail?: string;
	disabled?: boolean;
	className?: string;
}

/**
 * One list of the tenant's own people added to an email a customer receives,
 * with one choice of which line (To, CC or BCC) everyone on it goes on.
 *
 * PD-17100 — extracted from Settings > Notifications > Recipients, where each
 * customer document (invoice, delivery receipt, credit memo) edits a list like
 * this, so the storefront settings can edit the storefront's Website order
 * confirmation list with the same control. Pair it with toDocumentCopyList /
 * fromDocumentCopyList from @supply-mover-2-0/shared, which translate between
 * this single list and the per-line buckets the API stores.
 */
export function DocumentCopyListEditor({
	name,
	list,
	onChange,
	availableRoles,
	emptyRoles,
	emptyRoleConsequence,
	detail,
	disabled = false,
	className,
}: DocumentCopyListEditorProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();

	return (
		<div className={cn('space-y-4', className)}>
			{detail != null && detail !== '' ? (
				<p className={cn('text-xs', colors.classes.textSecondary)}>
					{t(detail)}
				</p>
			) : null}
			<div>
				<ButtonGroup
					name={name}
					label="Add them as"
					size="sm"
					value={list.mode}
					disabled={disabled}
					onChange={(value) => {
						onChange({ ...list, mode: value as CopyMode });
					}}
					options={[...DOCUMENT_COPY_LINE_OPTIONS]}
				/>
				<p className={cn('text-xs mt-1', colors.classes.textSecondary)}>
					{t(LINE_HINTS[list.mode])}
				</p>
			</div>
			<RecipientBucketEditor
				bucket={list.bucket}
				onChange={(bucket) => {
					onChange({ ...list, bucket });
				}}
				title={t('Recipients')}
				availableRoles={availableRoles}
				emptyRoles={emptyRoles}
				emptyRoleConsequence={emptyRoleConsequence}
				disabled={disabled}
			/>
		</div>
	);
}
